/**
 * The Values page: a person's values in their own words, the Projects and Bundles each value
 * leads, and applying a value to a group of activities with a change to their planned P and RJ
 * energy (undoable). Flux Agent does the same work in conversation; this page is the direct
 * editor. All rules are enforced by the server (`apps/api-rs/src/values.rs`); the page shows the
 * server's refusal message when a definition breaks one.
 *
 * @module apps/web/src/pages/ValuesPage
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Archive, Heart, Plus, RotateCcw, Sparkles, Undo2 } from 'lucide-react';
import type {
  ApplicationReceipt,
  EnergyAdjustment,
  StructureResponse,
  UserValue,
  ValueEnergyKind,
  ValueInput,
} from '@fluxability/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StructureTreeView } from '@/components/values/StructureTreeView';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

/** Native select styling matching the shared input. */
const SELECT_CLASS =
  'h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

/** Empty definition form. */
const EMPTY_FORM = {
  /** The person's one word. */
  name: '',
  /** The emotion, as a sensation. */
  emotion: '',
  /** Rule 1: what the body does. */
  bodyRule: '',
  /** Rule 2: what they say to themselves or put first. */
  meaningRule: '',
  /** Which energy the value mainly fuels, or empty. */
  kind: '' as '' | ValueEnergyKind,
  /** Energy of the word, 1–10, as typed. */
  rating: '',
};

/** Parse an optional number field; blank is undefined. */
function optionalNumber(text: string): number | undefined {
  if (!text.trim()) return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
}

/** Define a value: a word, the emotion, and one or two rules (body, then meaning). */
function ValueForm({
  onSaved,
}: {
  /** Called after a value is saved, to reload the list. */
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof EMPTY_FORM) => (value: string) =>
    setForm(current => ({ ...current, [key]: value }));

  /** Send the definition; the server checks the one-word name and rule rules. */
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const input: ValueInput = {
      name: form.name,
      emotion: form.emotion,
      rules: [form.bodyRule, form.meaningRule].filter(rule => rule.trim()),
      energy: {
        kind: form.kind || undefined,
        rating: optionalNumber(form.rating),
      },
    };
    const response = await api.values.create(input);
    setBusy(false);
    if (!response.success) {
      setError(response.error?.message || 'Could not save that value.');
      return;
    }
    toast({ title: `${response.data?.value.name} saved` });
    setForm(EMPTY_FORM);
    onSaved();
  }

  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-2" aria-label="New value">
      <div className="space-y-1">
        <Label htmlFor="value-name">Your word</Label>
        <Input
          id="value-name"
          value={form.name}
          onChange={event => set('name')(event.target.value)}
          placeholder="One word, yours"
          maxLength={48}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="value-emotion">What you feel</Label>
        <Input
          id="value-emotion"
          value={form.emotion}
          onChange={event => set('emotion')(event.target.value)}
          placeholder="The sensation, in your words"
          maxLength={400}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="value-body-rule">Rule 1: your body</Label>
        <Input
          id="value-body-rule"
          value={form.bodyRule}
          onChange={event => set('bodyRule')(event.target.value)}
          placeholder="When I'm living it, my body…"
          maxLength={400}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="value-meaning-rule">Rule 2: what you say or put first</Label>
        <Input
          id="value-meaning-rule"
          value={form.meaningRule}
          onChange={event => set('meaningRule')(event.target.value)}
          placeholder="I tell myself… / I put … first"
          maxLength={400}
        />
      </div>
      <div className="flex flex-wrap items-end gap-3 sm:col-span-2">
        <div className="space-y-1">
          <Label htmlFor="value-kind">Mostly fuels</Label>
          <select
            id="value-kind"
            className={SELECT_CLASS}
            value={form.kind}
            onChange={event => set('kind')(event.target.value)}
          >
            <option value="">Not sure yet</option>
            <option value="productivity">Productivity (P)</option>
            <option value="rejuvenation">Rejuvenation (RJ)</option>
            <option value="both">Both</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="value-rating">Energy of the word, 1–10</Label>
          <Input
            id="value-rating"
            type="number"
            min={1}
            max={10}
            className="w-24"
            value={form.rating}
            onChange={event => set('rating')(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={busy} className="gap-2">
          <Plus className="h-4 w-4" aria-hidden />
          Save value
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm sm:col-span-2">
          {error}
        </p>
      )}
      <p className="text-muted-foreground text-xs sm:col-span-2">
        A value is your word for an energizing emotion, defined by what your body does and what you
        say to yourself. Keep other value words out of the rules: say what you actually do.
      </p>
    </form>
  );
}

/** One saved value with archive/restore. */
function ValueItem({
  value,
  onToggle,
}: {
  /** The value to show. */
  value: UserValue;
  /** Flip the value between active and archived; the page shows the change at once. */
  onToggle: (value: UserValue) => void;
}) {
  const archived = value.status === 'archived';
  /** Flip the value between active and archived. */
  function toggle() {
    onToggle(value);
  }
  return (
    <li
      className={`rounded-lg border p-3 ${archived ? 'opacity-60' : ''}`}
      data-testid={`value-${value.name}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{value.name}</p>
          <p className="text-muted-foreground text-sm">{value.emotion}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggle}
          aria-label={archived ? `Restore ${value.name}` : `Archive ${value.name}`}
        >
          {archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
        </Button>
      </div>
      <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-sm">
        {value.rules.map(rule => (
          <li key={rule}>{rule}</li>
        ))}
      </ol>
      <p className="text-muted-foreground mt-2 text-xs">
        {[
          value.energy.kind &&
            { productivity: 'Fuels P', rejuvenation: 'Fuels RJ', both: 'Fuels P and RJ' }[
              value.energy.kind
            ],
          value.energy.rating && `Energy ${value.energy.rating}/10`,
          value.source === 'flux_agent' && 'Saved with Flux Agent',
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </li>
  );
}

/** Apply a value to a Bundle or Project and change its activities' planned energy. */
function ApplyPanel({
  values,
  structure,
  onApplied,
}: {
  /** Active values to choose from. */
  values: UserValue[];
  /** The tree, for choosing a Bundle or Project. */
  structure: StructureResponse | null;
  /** Called after an application or undo, to reload. */
  onApplied: () => void;
}) {
  const { toast } = useToast();
  const [valueId, setValueId] = useState('');
  const [target, setTarget] = useState('');
  const [mode, setMode] = useState<'shift' | 'set'>('shift');
  const [fields, setFields] = useState({ p: '', rj: '', pReq: '', rjReq: '' });
  const [receipt, setReceipt] = useState<ApplicationReceipt | null>(null);
  const [error, setError] = useState('');
  const groups = useMemo(() => {
    const options: { key: string; label: string }[] = [];
    for (const project of structure?.tree.projects ?? []) {
      if (project.archived) continue;
      options.push({ key: `project:${project.id}`, label: `${project.name} (all Bundles)` });
      for (const bundle of project.bundles)
        if (!bundle.archived)
          options.push({ key: `bundle:${bundle.id}`, label: `${project.name} › ${bundle.name}` });
    }
    for (const bundle of structure?.tree.loose_bundles ?? [])
      if (!bundle.archived) options.push({ key: `bundle:${bundle.id}`, label: bundle.name });
    return options;
  }, [structure]);

  /** Send the application; blank energy fields leave those numbers alone. */
  async function apply(event: FormEvent) {
    event.preventDefault();
    setError('');
    const [kind, id] = target.split(':') as ['bundle' | 'project', string];
    const energy: EnergyAdjustment = {
      mode,
      productivity_return: optionalNumber(fields.p),
      rejuvenation_return: optionalNumber(fields.rj),
      required_productivity_energy: optionalNumber(fields.pReq),
      required_rejuvenation_energy: optionalNumber(fields.rjReq),
    };
    const response = await api.values.apply({ value_id: valueId, targets: [{ kind, id }], energy });
    if (!response.success || !response.data) {
      setError(response.error?.message || 'Could not apply that value.');
      return;
    }
    setReceipt(response.data.application);
    toast({
      title: 'Value applied',
      description: `${response.data.application.changes.length} activities re-planned.`,
    });
    onApplied();
  }

  /** Undo the last application shown. */
  async function undo() {
    if (!receipt) return;
    const response = await api.values.undo(receipt.application_id);
    if (response.success) {
      toast({
        title: 'Undone',
        description: `${response.data?.undo.restored ?? 0} activities restored.`,
      });
      setReceipt(null);
      onApplied();
    }
  }

  const field = (key: keyof typeof fields, label: string) => (
    <div className="space-y-1">
      <Label htmlFor={`apply-${key}`}>{label}</Label>
      <Input
        id={`apply-${key}`}
        type="number"
        step={0.5}
        min={mode === 'set' ? 0 : -10}
        max={10}
        className="w-24"
        value={fields[key]}
        onChange={event => setFields(current => ({ ...current, [key]: event.target.value }))}
      />
    </div>
  );

  return (
    <form onSubmit={apply} className="space-y-3" aria-label="Apply a value">
      <div className="flex flex-wrap gap-3">
        <select
          aria-label="Value"
          className={SELECT_CLASS}
          value={valueId}
          onChange={event => setValueId(event.target.value)}
          required
        >
          <option value="">Choose a value</option>
          {values.map(value => (
            <option key={value.id} value={value.id}>
              {value.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Group"
          className={SELECT_CLASS}
          value={target}
          onChange={event => setTarget(event.target.value)}
          required
        >
          <option value="">Choose a Bundle or Project</option>
          {groups.map(group => (
            <option key={group.key} value={group.key}>
              {group.label}
            </option>
          ))}
        </select>
        <select
          aria-label="How to change energy"
          className={SELECT_CLASS}
          value={mode}
          onChange={event => setMode(event.target.value as 'shift' | 'set')}
        >
          <option value="shift">Add to the current numbers</option>
          <option value="set">Set the numbers</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-3">
        {field('p', 'P return')}
        {field('rj', 'RJ return')}
        {field('pReq', 'P it takes')}
        {field('rjReq', 'RJ it takes')}
      </div>
      <p className="text-muted-foreground text-xs">
        Leave a number blank to keep it. With every number blank, the value's own prediction is
        used, if you gave one. Completed and Team activities keep their numbers.
      </p>
      <div className="flex gap-2">
        <Button type="submit" className="gap-2">
          <Heart className="h-4 w-4" aria-hidden />
          Apply value
        </Button>
        {receipt && (
          <Button type="button" variant="outline" onClick={undo} className="gap-2">
            <Undo2 className="h-4 w-4" aria-hidden />
            Undo ({receipt.changes.length})
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
    </form>
  );
}

/** The Values page. */
export function ValuesPage() {
  const [values, setValues] = useState<UserValue[]>([]);
  const [structure, setStructure] = useState<StructureResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { toast } = useToast();
  // Counts archive/restore edits; a read that started before the latest edit is stale, and that
  // edit's own reload replaces it.
  const edits = useRef(0);

  const load = useCallback(async () => {
    const started = edits.current;
    const [list, tree] = await Promise.all([api.values.list(), api.values.structure()]);
    if (edits.current !== started) return;
    if (list.success && list.data) setValues(list.data.values);
    if (tree.success && tree.data) setStructure(tree.data);
    setLoaded(true);
  }, []);

  /**
   * Archive or restore `value`: show the new status at once, save it, then reload the list and
   * structure. A rejected save restores the previous status and shows the error.
   */
  const toggleArchived = useCallback(
    async (value: UserValue) => {
      const status = value.status === 'archived' ? 'active' : 'archived';
      const edit = ++edits.current;
      const show = (next: UserValue['status']) =>
        setValues(current =>
          current.map(item => (item.id === value.id ? { ...item, status: next } : item))
        );
      show(status);
      const response = await api.values.update(value.id, { status });
      if (!response.success) {
        show(value.status);
        toast({
          title: `${value.name} was not ${status === 'archived' ? 'archived' : 'restored'}`,
          description: response.error?.message || 'Try again in a moment.',
          variant: 'destructive',
        });
        return;
      }
      // Only the latest edit reloads; an earlier edit's reload would be discarded anyway.
      if (edits.current === edit) void load();
    },
    [load, toast]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const active = values.filter(value => value.status === 'active');

  /** Link a Project's or Bundle's leading value. */
  async function setNodeValue(kind: 'project' | 'bundle', id: string, valueId: string) {
    await api.values.link({ target_kind: kind, target_id: id, value_id: valueId });
    await load();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Values</h1>
        <p className="text-muted-foreground text-sm">
          The words that give you energy, the areas of life they lead, and the energy they bring to
          your activities.
        </p>
        <Link
          to="/agent"
          className="text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline"
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          Work through your values or your Projects with Flux Agent
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Your values</CardTitle>
          <CardDescription>Few, each clearly defined, in your own words.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ValueForm onSaved={load} />
          {loaded && !values.length && (
            <p className="text-muted-foreground text-sm">No values yet.</p>
          )}
          <ul className="grid gap-3 sm:grid-cols-2">
            {values.map(value => (
              <ValueItem key={value.id} value={value} onToggle={toggleArchived} />
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Projects and Bundles</CardTitle>
          <CardDescription>
            Each area of life you lead, the value leading it, and what is worth tidying.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {structure && (
            <StructureTreeView
              tree={structure.tree}
              findings={structure.findings}
              renderControl={(kind, node) =>
                active.length > 0 && (
                  <select
                    aria-label={`Value for ${node.name}`}
                    className={`${SELECT_CLASS} h-7 text-xs`}
                    value={node.value_id ?? ''}
                    onChange={event => void setNodeValue(kind, node.id, event.target.value)}
                  >
                    <option value="">No value</option>
                    {active.map(value => (
                      <option key={value.id} value={value.id}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                )
              }
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Apply a value to a group</CardTitle>
          <CardDescription>
            Bring one value to every open activity in a Bundle or Project, and re-plan their P and
            RJ energy with it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ApplyPanel values={active} structure={structure} onApplied={load} />
        </CardContent>
      </Card>
    </div>
  );
}
